// Deterministic compliance evaluation (client-side mirror of the backend engine)

export const STATUS = {
  not_assessed: { label: 'Not Assessed', tone: 'neutral', order: 99, next: 'Create or initialize assessment' },
  on_hold: { label: 'On Hold', tone: 'violet', order: 0, next: 'Resolve hold through authorized workflow' },
  rejected: { label: 'Rejected', tone: 'rose', order: 1, next: 'Return rework URL with rejected items' },
  pending_information: { label: 'Pending Information', tone: 'amber', order: 2, next: 'Return secure request URL' },
  submitted: { label: 'Submitted', tone: 'sky', order: 3, next: 'Reviewer action' },
  in_review: { label: 'In Review', tone: 'sky', order: 3, next: 'Complete decision within SLA' },
  expired: { label: 'Expired / Non-compliant', tone: 'rose', order: 4, next: 'Request replacement' },
  expiring_soon: { label: 'Expiring Soon', tone: 'orange', order: 5, next: 'Renew document' },
  compliant: { label: 'Compliant', tone: 'emerald', order: 6, next: 'Monitor validity and refresh events' },
  cancelled: { label: 'Cancelled', tone: 'neutral', order: 100, next: 'Create a new assessment if needed' },
};

export const ITEM_RESULT = {
  satisfied: { label: 'Satisfied', tone: 'emerald' },
  reused: { label: 'Reused', tone: 'teal' },
  waived: { label: 'Waived', tone: 'violet' },
  pending_review: { label: 'Pending review', tone: 'sky' },
  missing: { label: 'Missing', tone: 'amber' },
  rejected: { label: 'Rejected', tone: 'rose' },
  expired: { label: 'Expired', tone: 'rose' },
  expiring: { label: 'Expiring soon', tone: 'orange' },
  not_applicable: { label: 'Not applicable', tone: 'neutral' },
  optional_missing: { label: 'Not provided (optional)', tone: 'neutral' },
};

export const TONE_CLASSES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  teal: 'bg-teal-50 text-teal-700 border-teal-200',
  sky: 'bg-sky-50 text-sky-700 border-sky-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const DOT_CLASSES = {
  emerald: 'bg-emerald-500', teal: 'bg-teal-500', sky: 'bg-sky-500', amber: 'bg-amber-500', orange: 'bg-orange-500', rose: 'bg-rose-500', violet: 'bg-violet-500', neutral: 'bg-slate-400',
};

const today = () => new Date().toISOString().slice(0, 10);

export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - new Date(today()).getTime();
  return Math.round(ms / 86400000);
};

export const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

// Very small condition evaluator: supports `CODE == "X"` and `CODE != "X"`
export function evaluateCondition(condition, record, fields) {
  if (!condition) return { applies: true, reason: 'No condition' };
  const m = condition.match(/^\s*([A-Z_]+)\s*(==|!=)\s*"([^"]*)"\s*$/);
  if (!m) return { applies: true, reason: `Unparsed condition kept applicable: ${condition}` };
  const [, code, op, expected] = m;
  const field = fields.find((f) => f.code === code);
  let actual = field ? record.fieldValues?.[field.id]?.value : undefined;
  if (code === 'RISK_BAND' && isEmpty(actual)) actual = record.riskBand;
  const eq = String(actual ?? '') === expected;
  const applies = op === '==' ? eq : !eq;
  return { applies, reason: `${code} = "${actual ?? ''}" → ${condition} is ${applies}` };
}

export function getPublishedVersion(cls, checkDate = today()) {
  const published = (cls.versions || []).filter((v) => v.status === 'published' || v.status === 'superseded');
  const applicable = published.filter((v) => v.effectiveFrom <= checkDate && (!v.effectiveTo || v.effectiveTo >= checkDate));
  applicable.sort((a, b) => b.version - a.version);
  return applicable[0] || published.sort((a, b) => b.version - a.version)[0] || null;
}

export function latestEvidenceFor(evidence, recordId, documentId) {
  const list = evidence.filter((e) => e.recordId === recordId && e.documentId === documentId && e.status !== 'superseded');
  list.sort((a, b) => b.version - a.version);
  return list[0] || null;
}

export function evaluateAssessment({ record, cls, version, evidence, fields, documents, meta, settings, checkDate = today() }) {
  if (!version) return { status: 'not_assessed', items: [], blocking: [], ruleSetVersion: null };
  const expiringDays = settings?.reminders?.expiringSoonDays ?? 30;
  const waivers = meta?.waivers || [];
  const reqs = version.requirements.filter((r) => r.effectiveFrom <= checkDate && (!r.effectiveTo || r.effectiveTo >= checkDate));

  const items = reqs.map((r) => {
    const def = r.itemType === 'field' ? fields.find((f) => f.id === r.itemId) : documents.find((dd) => dd.id === r.itemId);
    const base = { requirementId: r.id, itemType: r.itemType, itemId: r.itemId, name: def?.name || r.itemId, code: def?.code, necessity: r.necessity, alternativeGroup: r.alternativeGroup, instructions: r.instructions };
    const cond = evaluateCondition(r.condition, record, fields);
    if (r.necessity === 'conditional' && !cond.applies) return { ...base, result: 'not_applicable', reason: cond.reason };
    const waiver = waivers.find((w) => w.itemId === r.itemId && (!w.expiresAt || w.expiresAt >= checkDate));
    if (waiver) return { ...base, result: 'waived', reason: `Waived by ${waiver.approver}: ${waiver.reason}`, waiver };

    if (r.itemType === 'field') {
      const fvv = record.fieldValues?.[r.itemId];
      if (!fvv || isEmpty(fvv.value) || fvv.status === 'missing') return { ...base, result: r.necessity === 'mandatory' || r.necessity === 'conditional' ? 'missing' : 'optional_missing', reason: 'No value supplied' };
      if (fvv.status === 'rejected') return { ...base, result: 'rejected', reason: fvv.rejectionReason || 'Value rejected by reviewer' };
      if (fvv.status === 'pending_review') return { ...base, result: 'pending_review', reason: 'Awaiting reviewer decision' };
      return { ...base, result: 'satisfied', reason: `Approved value (source: ${fvv.source})`, value: fvv.value };
    }

    const ev = latestEvidenceFor(evidence, record.id, r.itemId);
    if (!ev) return { ...base, result: r.necessity === 'optional' || r.necessity === 'informational' ? 'optional_missing' : 'missing', reason: 'No document uploaded' };
    const reused = ev.satisfiesClasses && ev.satisfiesClasses.length > 1 && ev.satisfiesClasses[0] !== cls.id;
    if (ev.status === 'rejected') return { ...base, result: 'rejected', reason: `${ev.rejectionCode}: ${ev.rejectionReason}`, evidenceId: ev.id };
    if (ev.status === 'pending_review' || ev.status === 'quarantined') return { ...base, result: 'pending_review', reason: 'Awaiting reviewer decision', evidenceId: ev.id };
    if (ev.status === 'approved') {
      const du = daysUntil(ev.expiryDate);
      if (du !== null && du < 0) return { ...base, result: 'expired', reason: `Expired ${Math.abs(du)} day(s) ago`, evidenceId: ev.id, expiryDate: ev.expiryDate };
      if (du !== null && du <= expiringDays) return { ...base, result: 'expiring', reason: `Expires in ${du} day(s)`, evidenceId: ev.id, expiryDate: ev.expiryDate, reused };
      return { ...base, result: reused ? 'reused' : 'satisfied', reason: reused ? `Reused from ${ev.satisfiesClasses[0].replace('c_', '').toUpperCase()} — approved ${ev.reviewedAt?.slice(0, 10)}` : `Approved by ${ev.reviewedBy} on ${ev.reviewedAt?.slice(0, 10)}`, evidenceId: ev.id, expiryDate: ev.expiryDate, reused };
    }
    return { ...base, result: 'missing', reason: 'Unknown evidence state', evidenceId: ev.id };
  });

  // Alternative groups: "one of" — if any member is satisfied the others become not_applicable
  const groups = {};
  items.forEach((it) => { if (it.alternativeGroup) (groups[it.alternativeGroup] = groups[it.alternativeGroup] || []).push(it); });
  Object.values(groups).forEach((members) => {
    const ok = members.find((m) => ['satisfied', 'reused', 'waived', 'expiring'].includes(m.result));
    const inFlight = members.find((m) => ['pending_review', 'rejected'].includes(m.result));
    members.forEach((m) => {
      if (ok && m !== ok) { m.result = 'not_applicable'; m.reason = `Alternative satisfied by ${ok.name}`; }
      else if (!ok && inFlight && m !== inFlight && m.result === 'missing') { m.result = 'not_applicable'; m.reason = `Alternative in progress via ${inFlight.name}`; }
      else if (!ok && !inFlight && m.result === 'missing') { m.reason = `Provide one of: ${members.map((x) => x.name).join(' / ')}`; }
    });
    if (!ok && !inFlight) {
      // keep only first as blocking to avoid double counting
      members.slice(1).forEach((m) => { if (m.result === 'missing') { m.result = 'not_applicable'; m.reason = `Covered by alternative group ${m.alternativeGroup}`; } });
    }
  });

  const blockingResults = ['rejected', 'missing', 'pending_review', 'expired'];
  const blocking = items.filter((i) => blockingResults.includes(i.result) && ['mandatory', 'conditional'].includes(i.necessity));

  let status = 'compliant';
  if (record.onHold) status = 'on_hold';
  else if (blocking.some((i) => i.result === 'rejected')) status = 'rejected';
  else if (blocking.some((i) => i.result === 'missing')) status = 'pending_information';
  else if (blocking.some((i) => i.result === 'pending_review')) status = meta?.claimedBy ? 'in_review' : 'submitted';
  else if (blocking.some((i) => i.result === 'expired')) status = 'expired';
  else if (items.some((i) => i.result === 'expiring' && ['mandatory', 'conditional'].includes(i.necessity))) status = 'expiring_soon';

  const validUntil = items.filter((i) => i.expiryDate && ['satisfied', 'reused', 'expiring'].includes(i.result)).map((i) => i.expiryDate).sort()[0] || null;
  const viaException = items.some((i) => i.result === 'waived');
  return { status, items, blocking, ruleSetVersion: version.version, validUntil, viaException, assessedAt: new Date().toISOString() };
}

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const relTime = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const abs = Math.abs(diff);
  const s = diff >= 0 ? 'ago' : 'from now';
  if (abs < 60) return 'just now';
  if (abs < 3600) return `${Math.round(abs / 60)}m ${s}`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${s}`;
  return `${Math.round(abs / 86400)}d ${s}`;
};
export const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
export const maskValue = (v) => { const s = String(v ?? ''); return s.length <= 4 ? '••••' : '•'.repeat(Math.max(4, s.length - 4)) + s.slice(-4); };
export const genToken = () => Array.from({ length: 32 }, () => 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'[Math.floor(Math.random() * 57)]).join('');
export const genId = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;
